import React, { useState, useEffect, useCallback } from 'react';
import { Table, Button, Badge, Spinner, Alert, Card } from 'react-bootstrap';
import axios from 'axios';
import moment from 'moment';
import BankIcon from '../accounts/BankIcon';

const OrgPlaidConnections = ({ org }) => {
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);
  const [error, setError] = useState(null);

  const fetchConnections = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get('/api/admin/plaid/connections');
      
      // Filter connections to only projects that belong to this Organization
      const orgProjectIds = org.projects ? org.projects.map(p => p._id.toString()) : [];
      const filtered = res.data.filter(conn => 
        conn.project && orgProjectIds.includes(conn.project.id.toString())
      );
      
      setConnections(filtered);
    } catch (err) {
      console.error('Error fetching connections:', err);
      setError('Failed to load bank connections.');
    } finally {
      setLoading(false);
    }
  }, [org]);

  useEffect(() => {
    fetchConnections();
  }, [fetchConnections]);

  const handleCleanup = async (connectionId, institutionName, projectName) => {
    if (!window.confirm(`Are you sure you want to clean up the connection to ${institutionName} for project "${projectName}"?\n\nThis will revoke the access token from Plaid and unlink any database accounts referencing it.`)) {
      return;
    }

    setActionLoading(connectionId);
    try {
      await axios.delete(`/api/admin/plaid/connections/${connectionId}`);
      fetchConnections();
    } catch (err) {
      console.error('Error cleaning up connection:', err);
      alert('Failed to delete bank connection.');
    } finally {
      setActionLoading(null);
    }
  };

  if (loading && connections.length === 0) {
    return (
      <div className="text-center p-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-2 text-muted">Retrieving bank connections for this organization...</p>
      </div>
    );
  }

  const orphanedCount = connections.filter(c => !c.accounts || c.accounts.length === 0).length;

  return (
    <div className="org-plaid-connections">
      {error && <Alert variant="danger">{error}</Alert>}

      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h4 className="font-weight-bold text-dark h5 mb-1">Organization Plaid Connections</h4>
          <p className="text-muted small mb-0">
            View active bank tokens and linked ledger accounts for projects in this organization.
          </p>
        </div>
        <Button variant="outline-primary" size="sm" onClick={fetchConnections} disabled={loading}>
          {loading ? <i className="fas fa-sync fa-spin mr-1"></i> : <i className="fas fa-sync mr-1"></i>} Refresh
        </Button>
      </div>

      {orphanedCount > 0 && (
        <Alert variant="warning" className="d-flex align-items-center border-0 shadow-sm mb-4" style={{ borderRadius: '8px' }}>
          <i className="fas fa-exclamation-triangle fa-2x text-warning mr-3"></i>
          <div>
            <h6 className="font-weight-bold text-warning-dark mb-1">Orphaned Tokens Found</h6>
            <p className="small mb-0 text-muted">
              There are <strong>{orphanedCount}</strong> bank connection(s) that are not linked to any accounts in the database. 
              Clean them up to avoid recurring active subscription fees from Plaid.
            </p>
          </div>
        </Alert>
      )}

      {connections.length === 0 ? (
        <Card className="text-center py-5 border-0 bg-light">
          <Card.Body>
            <i className="fas fa-plug fa-3x mb-3 text-muted opacity-30"></i>
            <h5 className="font-weight-bold">No bank connections found</h5>
            <p className="text-muted small">No projects under this organization have linked bank accounts via Plaid yet.</p>
          </Card.Body>
        </Card>
      ) : (
        <div className="table-responsive shadow-sm rounded border bg-white">
          <Table hover className="mb-0 align-middle">
            <thead className="bg-light">
              <tr>
                <th>Project / Company</th>
                <th>Bank Connection</th>
                <th>Linked Accounts</th>
                <th width="120" className="text-center">Status</th>
                <th width="160">Connected On</th>
                <th width="130" className="text-right pr-4">Action</th>
              </tr>
            </thead>
            <tbody>
              {connections.map(conn => {
                const isOrphaned = !conn.accounts || conn.accounts.length === 0;
                
                return (
                  <tr key={conn._id} style={isOrphaned ? { backgroundColor: '#fffbeb' } : {}}>
                    <td className="font-weight-bold text-dark py-3">
                      <span className="d-block">{conn.project.name}</span>
                    </td>
                    <td>
                      <div className="d-flex align-items-center">
                        <BankIcon institutionName={conn.institutionName} logo={conn.logo} size="32px" />
                        <div className="ml-3">
                          <span className="font-weight-bold text-dark d-block">{conn.institutionName || 'Unknown Bank'}</span>
                          {isOrphaned && (
                            <span className="badge badge-warning text-uppercase px-2 py-1 font-weight-bold" style={{ fontSize: '0.65rem', color: '#854d0e', backgroundColor: '#fef9c3' }}>
                              Orphaned Token
                            </span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td>
                      {isOrphaned ? (
                        <span className="text-danger small font-weight-bold"><i className="fas fa-exclamation-circle mr-1"></i> 0 accounts linked</span>
                      ) : (
                        <div>
                          <span className="badge badge-info px-2 py-1 font-weight-bold" style={{ fontSize: '0.7rem', backgroundColor: '#e0f2fe', color: '#0369a1' }}>
                            {conn.accounts.length} Account(s)
                          </span>
                          <div className="text-muted x-small mt-1" style={{ maxWidth: '250px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {conn.accounts.map(a => a.name).join(', ')}
                          </div>
                        </div>
                      )}
                    </td>
                    <td className="text-center">
                      <span 
                        className={`badge badge-${conn.status === 'healthy' ? 'success' : 'danger'} text-uppercase px-2 py-1`}
                        style={{ fontSize: '0.7rem', borderRadius: '12px' }}
                      >
                        {conn.status}
                      </span>
                    </td>
                    <td>
                      <span className="small text-muted">{moment(conn.createdAt).format('MMM DD, YYYY') || 'Unknown'}</span>
                    </td>
                    <td className="text-right pr-4">
                      <Button 
                        variant={isOrphaned ? 'warning' : 'outline-danger'}
                        size="sm" 
                        className="font-weight-bold py-1 px-3 shadow-none"
                        style={{ borderRadius: '6px' }}
                        onClick={() => handleCleanup(conn._id, conn.institutionName, conn.project.name)}
                        disabled={actionLoading === conn._id}
                      >
                        {actionLoading === conn._id ? (
                          <Spinner size="sm" animation="border" />
                        ) : (
                          <><i className="fas fa-trash-alt mr-1"></i> Clean Up</>
                        )}
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </div>
      )}
    </div>
  );
};

export default OrgPlaidConnections;
